<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { TuButton, TuInputText } from "@tinyurldev/tu-ui-vue";

import { logError } from "@Resources/js/helpers/logger";
import SanitizeDomainString from "@Modules/custom-domain/helpers/SanitizeDomainString";
import XOverlay from "@Modules/shared/store/XOverlay";
import { XAuthStore } from "@Modules/users/Store/XAuthStore";
import { useTranslation } from "@Resources/js/composables";
import type { BrandedDomainCheckResult } from "@Modules/teams/Api/DomainsApi";
import PopoverOverhauling from "@Components/popover/PopoverOverhauling.vue";

import CheckSubDomainAdded from "./CheckSubDomain.vue";

//
// Setup
const overlay = XOverlay();
const authStore = XAuthStore();
const { __ } = useTranslation();

//
// Props
interface Props {
  errors: Record<string, string>;
  loading: boolean;
  personalDomain?: string;
  createSubDomain: (domain: string) => Promise<boolean>;
  checkSubDomain: (domain: string) => Promise<BrandedDomainCheckResult>;
}

const props = defineProps<Props>();

//
// Emit
interface Events {
  (e: "domain-created", domain: string): void;
}

const emit = defineEmits<Events>();

//
// Data
const domainFormVisible = ref(true);
const createdDomain = ref<{ domain: string } | null>(null);
const form = reactive({
  baseDomain: "",
  subDomain: "",
  busy: false,
  errors: {} as Record<string, string>,
});

//
// Computed
const domain = computed(() => `${cleanDomainValue(form.subDomain)}.${cleanDomainValue(form.baseDomain)}`);
const baseValid = computed(() => /^(([a-z0-9][a-z0-9-]{0,60})?[a-z0-9]\.)+[a-z0-9]+$/i.test(cleanDomainValue(form.baseDomain)));
const subValid = computed(() => /^[a-z0-9][a-z0-9.-]*[a-z0-9]$/i.test(cleanDomainValue(form.subDomain)));
const isSubdomainVisible = computed(() => form.baseDomain.length >= 3);
const isPreviewVisible = computed(() => isSubdomainVisible.value && form.subDomain.length > 1);

//
// Methods
function cleanDomainValue(value: string): string {
  return SanitizeDomainString(value);
}

function closeForm(): void {
  if (form.busy || props.loading) {
    return;
  }

  overlay.hide();
}

async function create(): Promise<void> {
  form.errors = {};

  if (!baseValid.value) {
    form.errors.baseDomain = __("organization_domains_settings.byo_subdomain_popover.base_domain_error");
  }

  if (!subValid.value) {
    form.errors.subDomain = __("organization_domains_settings.byo_subdomain_popover.subdomain_error");
  }

  if (Object.keys(form.errors).length > 0) {
    return;
  }

  form.busy = true;

  try {
    const success = await props.createSubDomain(domain.value);

    if (success) {
      createdDomain.value = { domain: domain.value };
      domainFormVisible.value = false;
      authStore.refreshApplicationState();
      emit("domain-created", domain.value);
    }
  } catch {
    logError(`[BYOSubDomainPopover][create]: domain ${domain.value} could not be created.`);
  } finally {
    form.busy = false;
  }
}

//
// Exposed Test Vars
defineExpose({ form, domainFormVisible, createdDomain, baseValid, subValid });
</script>

<template>
  <PopoverOverhauling @close="overlay.hide()">
    <template #title>
      {{ __("organization_domains_settings.byo_subdomain_popover.title") }}
    </template>

    <div class="byo-subdomain-popover">
      <template v-if="domainFormVisible">
        <h5 class="byo-subdomain-popover__intro text-muted">
          {{ __("organization_domains_settings.byo_subdomain_popover.subtitle") }}
        </h5>

        <p class="byo-subdomain-popover__description">
          {{ __("organization_domains_settings.byo_subdomain_popover.description_1") }}
        </p>
        <p class="byo-subdomain-popover__description">
          {{ __("organization_domains_settings.byo_subdomain_popover.description_2") }}
        </p>

        <form
          class="byo-subdomain-popover__form"
          @submit.prevent="create"
        >
          <TuInputText
            id="baseDomain"
            v-model="form.baseDomain"
            class="_base-domain"
            :label="{ id: 'baseDomain', label: __('organization_domains_settings.byo_subdomain_popover.base_domain_label') }"
            :input="{ placeholder: 'yourdomain.com' }"
            :copy="{
              help: __('organization_domains_settings.byo_subdomain_popover.base_domain_hint'),
              errorMessage: form.errors.baseDomain,
            }"
            @focus="form.errors = {}"
          />

          <TuInputText
            v-show="isSubdomainVisible"
            id="subDomain"
            v-model="form.subDomain"
            class="_subdomain"
            :label="{ id: 'subDomain', label: __('organization_domains_settings.byo_subdomain_popover.subdomain_label') }"
            :copy="{
              help: __('organization_domains_settings.byo_subdomain_popover.subdomain_hint'),
              errorMessage: form.errors.subDomain || props.errors?.domain,
            }"
            @focus="form.errors = {}"
          />

          <div
            v-if="isPreviewVisible"
            class="byo-subdomain-popover__links-preview"
          >
            <small>
              {{ __("organization_domains_settings.byo_subdomain_popover.links_preview") }} <i>https://{{ domain }}/yourlink</i>
            </small>
          </div>

          <TuButton
            class="_create"
            type="submit"
            :label="__('organization_domains_settings.byo_subdomain_popover.create_button')"
            :is-loading="form.busy"
            :is-disabled="form.busy || props.loading"
            is-block
          />
          <TuButton
            class="_cancel"
            type="button"
            is-outlined
            :label="__('organization_domains_settings.byo_subdomain_popover.cancel_button')"
            :is-disabled="form.busy || props.loading"
            @click.stop="closeForm"
          />
        </form>
      </template>

      <template v-if="!domainFormVisible">
        <div class="byo-subdomain-popover__intro">
          <h5 class="text-muted">
            {{ __("organization_domains_settings.byo_subdomain_popover.subtitle") }}
          </h5>
          <h2 class="text-dark">
            {{ createdDomain?.domain }}
          </h2>
        </div>

        <CheckSubDomainAdded
          :domain="createdDomain?.domain"
          :personal-domain="personalDomain"
          :check-sub-domain="checkSubDomain"
        />

        <TuButton
          class="_finish"
          type="button"
          is-outlined
          :label="__('organization_domains_settings.byo_subdomain_popover.finish_button')"
          @click.stop="overlay.hide()"
        />
      </template>
    </div>
  </PopoverOverhauling>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;

.byo-subdomain-popover {
  display: flex;
  flex-flow: column;
  gap: spacing.$xxs;

  &__form {
    display: flex;
    flex-flow: column;
    gap: spacing.$xxs;
  }

  &__intro,
  &__description {
    margin: 0;
  }
}
</style>
